import { createSlice } from '@reduxjs/toolkit';
import BaseApiState, { createBaseAPi } from '@/api/BaseApiState';
import {
    cancelScheduledPlanSwitch,
    getGoLiveQuote,
    getMonetisationPlans,
    getSubscriptionQuote,
    getVenueMonetisation,
    subscribeVenue,
    switchVenuePlan,
} from '../thunkMidddleware/monetisationMiddleware';

/**
 * Per-venue plan state.
 *
 * `venuePlans` is keyed by venue id rather than held as one "current venue"
 * slot, because the sidebar reads a plan for whichever venue the route is on
 * while other screens may be looking at a different one.
 */

interface MonetisationState {
    plansResponse: createBaseAPi;
    /** venueId -> that venue's monetisation record. */
    venuePlans: Record<string, any>;
    /**
     * Which venue the sidebar's plan card is describing. The route supplies it
     * on venue-scoped screens; elsewhere the last one stays put so the card
     * does not change venue under the vendor as they navigate.
     */
    selectedVenueId: string | null;
    selectedVenueName: string | null;
    venuePlanResponse: createBaseAPi;
    goLiveQuoteResponse: createBaseAPi;
    quoteResponse: createBaseAPi;
    subscribeResponse: createBaseAPi;
    switchPlanResponse: createBaseAPi;
    cancelSwitchResponse: createBaseAPi;
}

const initialState: MonetisationState = {
    plansResponse: { ...BaseApiState },
    venuePlans: {},
    selectedVenueId: null,
    selectedVenueName: null,
    venuePlanResponse: { ...BaseApiState },
    goLiveQuoteResponse: { ...BaseApiState },
    quoteResponse: { ...BaseApiState },
    subscribeResponse: { ...BaseApiState },
    switchPlanResponse: { ...BaseApiState },
    cancelSwitchResponse: { ...BaseApiState },
};

/** Every thunk writes the same five fields, so the wiring is generated. */
const attach = (
    builder: any,
    thunk: any,
    key: keyof MonetisationState,
    onFulfilled?: (state: MonetisationState, payload: any, arg: any) => void,
) => {
    builder
        .addCase(thunk.pending, (state: MonetisationState) => {
            const slot = state[key] as createBaseAPi;
            slot.isLoading = true;
            slot.error = null;
            slot.success = false;
            slot.message = null;
        })
        .addCase(thunk.fulfilled, (state: MonetisationState, action: any) => {
            const slot = state[key] as createBaseAPi;
            const { payload, message } = action.payload || {};
            slot.isLoading = false;
            slot.success = true;
            slot.payload = payload;
            slot.message = message;
            onFulfilled?.(state, payload, action.meta?.arg);
        })
        .addCase(thunk.rejected, (state: MonetisationState, action: any) => {
            const slot = state[key] as createBaseAPi;
            slot.isLoading = false;
            slot.success = false;
            slot.error = action.payload;
        });
};

const monetisationSlice = createSlice({
    name: 'monetisation',
    initialState,
    reducers: {
        setPlanVenue: (state, action) => {
            state.selectedVenueId = action.payload?.venueId ?? null;
            state.selectedVenueName = action.payload?.venueName ?? null;
        },
        resetSubscribeState: (state) => {
            state.subscribeResponse = { ...BaseApiState };
        },
        resetSwitchPlanState: (state) => {
            state.switchPlanResponse = { ...BaseApiState };
            state.cancelSwitchResponse = { ...BaseApiState };
        },
    },
    extraReducers: (builder) => {
        attach(builder, getMonetisationPlans, 'plansResponse');
        attach(builder, getGoLiveQuote, 'goLiveQuoteResponse');
        attach(builder, getSubscriptionQuote, 'quoteResponse');
        attach(builder, subscribeVenue, 'subscribeResponse');

        // The venue record is cached by id so the sidebar can read it without
        // refetching every time the route changes.
        attach(builder, getVenueMonetisation, 'venuePlanResponse', (state, payload) => {
            if (payload?.venueId) state.venuePlans[payload.venueId] = payload;
        });

        // A switch only rewrites the cached record when it actually took
        // effect — a 202 PAYMENT_REQUIRED leaves the venue on its old plan.
        attach(builder, switchVenuePlan, 'switchPlanResponse', (state, payload, arg) => {
            const venueId = arg?.venueId;
            if (!venueId || !payload || !state.venuePlans[venueId]) return;
            state.venuePlans[venueId] = {
                ...state.venuePlans[venueId],
                plan: payload.plan ?? state.venuePlans[venueId].plan,
                pendingPlanChange: payload.pendingPlanChange ?? null,
                mandate: payload.mandate ?? state.venuePlans[venueId].mandate,
            };
        });

        attach(builder, cancelScheduledPlanSwitch, 'cancelSwitchResponse');
    },
});

export const { setPlanVenue, resetSubscribeState, resetSwitchPlanState } = monetisationSlice.actions;
export default monetisationSlice.reducer;
